import { useState, useEffect, useRef, useMemo } from 'react';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { formatDate, parseISO } from 'date-fns';
import { Link, useSearchParams } from 'react-router-dom';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import {
    fetchCampaignApplications,
    fetchCampaignSearchFilters,
    fetchPatientServicesCoordinators,
} from '../../services/campaign';
import { useQueries } from '@tanstack/react-query';
import { FaSearch, FaSync, FaTimes } from 'react-icons/fa';
import { ReactSelectInput } from '~/common/form/Inputs';
import { Pagination } from '~/components/Pagination';
import { useCacheInvalidation } from '~/utils/cacheInvalidation';
import { formatDateIfValid } from '~/utils';
import { useAuth } from '~/context/AuthProvider';

const debounce = (
    func: (name: string, value: string) => void,
    delay: number
) => {
    let timeoutId: NodeJS.Timeout;
    return (name: string, value: string) => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
            func(name, value);
        }, delay);
    };
};

type CampaignApplication = {
    id: number;
    campaign: number;
    campaign_name: string;
    campaign_ntaf_id: string;
    campaign_status: {
        code: string;
        name: string;
    } | null;
    campaign_is_for: {
        id: number;
        code: string;
        name: string;
    } | null;
    patient_name: string;
    pc_first_name: string;
    pc_last_name: string;
    pc_phone_cell: string;
    pc_email: string;
    mp_signature_date: string | null;
    fin_signature_date: string | null;
    ag_signature_date: string | null;
    sections_completed: {
        completed: number;
        total: number;
    };
    campaign_csc: string;
    campaign_medical_reason: string;
    campaign_medical_issue: string;
    campaign_approved_date: string | null;
    update_date: string;
    created_date: string;
};

const columnHelper = createColumnHelper<CampaignApplication>();

enum Sort {
    ASC = 'asc',
    DESC = 'desc',
}

type FilterOption = {
    label: string;
    value: string;
};

type MedicalIssue = {
    id: number;
    name: string;
    medical_reason: {
        id: number;
        name: string;
    };
};

type Filters = {
    medical_reason: FilterOption[];
    medical_issues: MedicalIssue[];
    csc: FilterOption[];
};

type SelectedFilters = {
    status: string;
    medical_reason: string;
    medical_issue: string;
    name: string;
    assignee: string;
};

const initialFilters: SelectedFilters = {
    status: '',
    medical_reason: '',
    medical_issue: '',
    name: '',
    assignee: '',
};

// Application-specific status filters
const APPLICATION_STATUS_FILTERS: FilterOption[] = [
    { label: 'Not Started', value: 'not_started' },
    { label: 'In Progress', value: 'in_progress' },
    { label: 'Completed', value: 'completed' },
    { label: 'Approved', value: 'approved' },
];

type DateRange = {
    start_date: string;
    end_date: string;
};

const initialDateRange = {
    start_date: '',
    end_date: '',
};

const CampaignApplications: React.FC = () => {
    const { refreshCampaignApplications } = useCacheInvalidation();
    const { person } = useAuth();
    const [searchParams] = useSearchParams();
    const pscDefaultApplied = useRef(false);
    const [pageSize] = useState(20);
    const [pages, setPages] = useState<number>(1);
    const [page, setPage] = useState<number>(1);
    const [sort, setSort] = useState<Sort | null>(null);
    const [filters, setFilters] = useState<Filters | null>(null);

    // URL params present = fresh navigation (e.g. dashboard "View All"); ignore localStorage so
    // stale filters don't restrict results. Otherwise, restore the user's saved filters.
    const urlAssignee = searchParams.get('assignee');
    const urlStatus = searchParams.get('status');
    const hasUrlParams = Boolean(urlAssignee || urlStatus);

    const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>(() => {
        if (hasUrlParams) {
            return {
                ...initialFilters,
                ...(urlAssignee && { assignee: urlAssignee }),
                ...(urlStatus && { status: urlStatus }),
            };
        }
        try {
            const savedFilters = localStorage.getItem('campaignApplicationFilters');
            return savedFilters ? { ...initialFilters, ...JSON.parse(savedFilters) } : initialFilters;
        } catch (error) {
            console.error('Error loading filters from localStorage:', error);
            return initialFilters;
        }
    });

    const [sortBy, setSortBy] = useState<string | null>(null);
    const [textInputResetKey, setTextInputResetKey] = useState(0);

    const [dateRange, setDateRange] = useState<DateRange>(() => {
        if (hasUrlParams) return initialDateRange;
        try {
            const savedDateRange = localStorage.getItem('campaignApplicationDateRange');
            return savedDateRange ? JSON.parse(savedDateRange) : initialDateRange;
        } catch (error) {
            console.error('Error loading date range from localStorage:', error);
            return initialDateRange;
        }
    });

    const results = useQueries({
        queries: [
            {
                queryKey: [
                    'campaign-applications',
                    { pageSize, sort, sortBy, page },
                    selectedFilters,
                    dateRange,
                ],
                queryFn: () =>
                    fetchCampaignApplications(
                        { pageSize, sort, sortBy },
                        selectedFilters,
                        dateRange,
                        page
                    ),
                staleTime: 0,
            },
            {
                queryKey: ['campaign_search_filters'],
                queryFn: () => fetchCampaignSearchFilters(),
                staleTime: 0,
            },
            {
                queryKey: ['patient_services_coordinators'],
                queryFn: () => fetchPatientServicesCoordinators(),
                staleTime: 0,
            },
        ],
    });

    const applicationResults = results[0]?.data;

    // For PSC (non-admin) users, default the assignee filter to themselves
    useEffect(() => {
        if (pscDefaultApplied.current || !person) return;

        pscDefaultApplied.current = true;

        const roles: string[] = person.roles || [];
        const isAdmin = roles.some(r => r.toLowerCase() === 'administrator');
        const isPSC = roles.some(r => r.toLowerCase() === 'patient services coordinator');

        if (!isAdmin && isPSC) {
            setSelectedFilters(prev => {
                if (prev.assignee) return prev;
                return { ...prev, assignee: String(person.id) };
            });
        }
    }, [person]);

    // Save filters to localStorage whenever they change
    useEffect(() => {
        try {
            localStorage.setItem('campaignApplicationFilters', JSON.stringify(selectedFilters));
        } catch (error) {
            console.error('Error saving filters to localStorage:', error);
        }
    }, [selectedFilters]);

    // Save date range to localStorage whenever it changes
    useEffect(() => {
        try {
            localStorage.setItem('campaignApplicationDateRange', JSON.stringify(dateRange));
        } catch (error) {
            console.error('Error saving date range to localStorage:', error);
        }
    }, [dateRange]);

    useEffect(() => {
        const total = applicationResults?.count;
        if (total) {
            setPages(Math.ceil(total / pageSize));
        }
    }, [applicationResults, pageSize]);

    useEffect(() => {
        const filterData = results[1]?.data;
        const pscData = results[2]?.data;
        if (filterData && pscData && !filters) {
            console.log('Setting filters from data:', filterData);
            // Handle both array and paginated response formats
            const pscList = Array.isArray(pscData) ? pscData : (pscData.results || []);
            
            const newFilters = {
                medical_reason:
                    filterData.medical_reason?.map(
                        (reason: { name: string; id: number }) => ({
                            label: reason.name,
                            value: String(reason.id),
                        })
                    ) || [],
                medical_issues: filterData.medical_issues || [],
                csc: pscList.map((psc: any) => ({
                    label: psc.full_name || '',
                    value: String(psc.person_id),
                })),
            };
            console.log('New filters:', newFilters);
            setFilters(newFilters);
        }
    }, [results, filters]);

    const isLoading = results.some((result) => result.isLoading);
    const isFetching = results.some((result) => result.isFetching);
    const error = results.find((result) => result.error);

    const handleSort = (columnId: string) => {
        if (sortBy === columnId) {
            if (sort === Sort.ASC) {
                setSort(Sort.DESC);
            } else {
                setSort(Sort.ASC);
            }
        } else {
            setSortBy(columnId);
            setSort(Sort.ASC);
        }
    };

    const handleFilter = (filterType: string, value: string) => {
        setPage(1);
        const newFilters = {
            ...selectedFilters,
            [filterType]: value,
        };

        // Clear medical_issue when medical_reason changes
        if (filterType === 'medical_reason') {
            newFilters.medical_issue = '';
        }

        setSelectedFilters(newFilters);
    };

    const handleDateRange = (dateRangeType: string, value: string) => {
        setPage(1);
        setDateRange({ ...dateRange, [dateRangeType]: value });
    };

    const debouncedHandleFilter = debounce((name: string, value: string) => {
        setPage(1);
        setSelectedFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    }, 500);

    const handleClearFilters = () => {
        setPage(1);
        setSelectedFilters(initialFilters);
        setDateRange(initialDateRange);
        localStorage.removeItem('campaignApplicationFilters');
        localStorage.removeItem('campaignApplicationDateRange');
        setTextInputResetKey((k) => k + 1);
    };

    const hasActiveFilters = Object.entries(selectedFilters).some(
        ([key, value]) => value !== initialFilters[key as keyof SelectedFilters]
    ) || dateRange.start_date || dateRange.end_date;

    const columns = useMemo(
        () => [
            columnHelper.accessor(
                (props) => formatDateIfValid(props.created_date, 'MM/dd/yy'),
                {
                    id: 'created_date',
                    header: () => 'Request Approved',
                    size: 100,
                    minSize: 100,
                    maxSize: 100,
                    cell: (props) => props.renderValue(),
                }
            ),
            columnHelper.accessor(
                (props) =>
                    props.update_date
                        ? formatDate(parseISO(props.update_date), 'MM/dd/yy')
                        : '',
                {
                    id: 'update_date',
                    header: () => 'Last Updated',
                    size: 100,
                    minSize: 100,
                    maxSize: 100,
                    cell: (props) => props.renderValue(),
                }
            ),
            columnHelper.accessor(
                (props) => {
                    const first = props.pc_first_name || '';
                    const last = props.pc_last_name || '';
                    return `${first} ${last}`.trim();
                },
                {
                    id: 'pc_name',
                    header: () => 'Contact',
                    size: 180,
                    minSize: 150,
                    maxSize: 200,
                    cell: (props) => {
                        const value = props.renderValue();
                        return (
                            <div>
                                <div>{value || ''}</div>
                                <div className='text-sm text-secondary'>
                                    {props.row.original.campaign_is_for
                                        ? `${props.row.original.campaign_is_for.name}`
                                        : ''}
                                </div>
                            </div>
                        );
                    },
                }
            ),
            columnHelper.accessor('patient_name', {
                header: () => 'Client',
                cell: (props) => {
                    const value = props.renderValue();
                    return (
                        <Link
                            to={`/campaign-applications/${props.row.original.id}`}
                            className='font-bold text-primary'
                        >
                            {value && value.trim() ? value : ''}
                        </Link>
                    );
                },
            }),
            columnHelper.accessor(
                (props) =>
                    props.sections_completed || { completed: 0, total: 6 },
                {
                    id: 'requirements',
                    header: () => 'Requirements',
                    size: 120,
                    minSize: 120,
                    maxSize: 120,
                    meta: {
                        style: {
                            width: '120px',
                            minWidth: '120px',
                            maxWidth: '120px',
                        },
                    },
                    cell: (props) => {
                        const data = props.getValue();
                        return (
                            <div className='flex items-center gap-2'>
                                <span
                                    className={`font-bold ${
                                        data.completed === data.total
                                            ? 'text-[#AFC12D]'
                                            : 'text-[#F02D3A]'
                                    }`}
                                >
                                    {data.completed} of {data.total}
                                </span>
                            </div>
                        );
                    },
                }
            ),
            columnHelper.accessor('campaign_csc', {
                header: () => 'CSC',
                cell: (props) => props.renderValue() || '',
            }),
            columnHelper.accessor('campaign_medical_reason', {
                header: () => 'Medical Reason',
                cell: (props) => {
                    const medicalReason = props.getValue();
                    const medicalIssue = props.row.original.campaign_medical_issue;
                    return (
                        <div>
                            <div>{medicalReason || ''}</div>
                            {medicalIssue && (
                                <div className='text-sm text-secondary'>
                                    {medicalIssue}
                                </div>
                            )}
                        </div>
                    );
                },
            }),
            ...(selectedFilters.status === 'approved'
                ? [
                      columnHelper.accessor(
                          (props) =>
                              props.campaign_approved_date
                                  ? formatDateIfValid(props.campaign_approved_date, 'MM/dd/yy')
                                  : '',
                          {
                              id: 'approved_date',
                              header: () => 'Application Approved',
                              size: 100,
                              minSize: 100,
                              maxSize: 100,
                              cell: (props) => props.renderValue() || '',
                          }
                      ),
                  ]
                : []),
        ],
        [selectedFilters.status]
    );

    const table = useReactTable({
        data: applicationResults?.results || [],
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    if (error) {
        console.error('Campaign Applications Error:', error);
        const errorData = (error as any)?.response?.data;
        return (
            <div className='p-8'>
                <h2 className='text-red-600 mb-4 text-xl font-bold'>
                    Error Loading Campaign Applications
                </h2>
                <div className='bg-red-50 border-red-200 rounded border p-4'>
                    <p className='mb-2'>
                        <strong>Error:</strong> {String(error)}
                    </p>
                    {errorData?.error && (
                        <div className='mt-4'>
                            <p className='mb-2 font-semibold'>Error Details:</p>
                            <pre className='max-h-96 overflow-auto rounded border bg-white p-3 text-xs'>
                                {errorData.error}
                            </pre>
                        </div>
                    )}
                    {errorData?.traceback && (
                        <div className='mt-4'>
                            <p className='mb-2 font-semibold'>Stack Trace:</p>
                            <pre className='max-h-96 overflow-auto rounded border bg-white p-3 text-xs'>
                                {errorData.traceback}
                            </pre>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <>
            <Breadcrumb pageName='Campaign Applications' />

            <section className='data-table-common rounded-sm border border-stroke bg-white py-4 shadow-default dark:border-strokedark dark:bg-boxdark'>
                <div className='flex flex-wrap items-center justify-start gap-4 overflow-x-auto px-8 pb-4'>
                    <div className='relative min-w-[175px] max-w-[175px]'>
                        <input
                            key={`contact-${textInputResetKey}`}
                            name='contact'
                            placeholder='Contact or Client'
                            className='h-[36px] w-full rounded border border-stroke px-3 pr-10 text-sm outline-none focus:border-primary'
                            defaultValue={selectedFilters.name}
                            onChange={(event) =>
                                debouncedHandleFilter(
                                    'name',
                                    event.target.value
                                )
                            }
                        />
                        <FaSearch
                            color='gray'
                            className='absolute right-3 top-1/2 -translate-y-1/2'
                        />
                    </div>
                    <div className='relative'>
                        {!filters?.csc ? (
                            <div className='w-[175px] animate-pulse'>
                                <div className='h-10 rounded bg-gray-4 dark:bg-gray-5'></div>
                            </div>
                        ) : (
                            <div className='min-w-[175px] max-w-[175px]'>
                                <ReactSelectInput
                                    options={filters.csc}
                                    value={selectedFilters.assignee}
                                    onChange={(value) =>
                                        handleFilter('assignee', value)
                                    }
                                    placeholder='Assigned CSC'
                                />
                            </div>
                        )}
                    </div>
                    <div className='relative'>
                        <div className='min-w-[140px]'>
                            <ReactSelectInput
                                options={APPLICATION_STATUS_FILTERS}
                                value={selectedFilters.status}
                                onChange={(value) =>
                                    handleFilter('status', value)
                                }
                                placeholder='Status'
                            />
                        </div>
                    </div>
                    <div className='relative'>
                        {!filters ? (
                            <div className='w-[180px] animate-pulse'>
                                <div className='h-4 rounded-full bg-gray-4 dark:bg-gray-5'></div>
                            </div>
                        ) : (
                            <div className='min-w-[180px] max-w-[180px]'>
                                <ReactSelectInput
                                    options={filters?.medical_reason || []}
                                    value={selectedFilters.medical_reason}
                                    onChange={(value) =>
                                        handleFilter('medical_reason', value)
                                    }
                                    placeholder='Medical Reason'
                                />
                            </div>
                        )}
                    </div>
                    {selectedFilters.medical_reason &&
                        filters?.medical_issues && (
                            <div className='relative'>
                                <div className='min-w-[175px] max-w-[175px]'>
                                    <ReactSelectInput
                                        options={filters.medical_issues
                                            .filter(
                                                (issue) =>
                                                    issue.medical_reason &&
                                                    String(issue.medical_reason.id) ===
                                                    String(selectedFilters.medical_reason)
                                            )
                                            .map((issue) => ({
                                                label: issue.name,
                                                value: String(issue.id),
                                            }))}
                                        value={selectedFilters.medical_issue}
                                        onChange={(value) =>
                                            handleFilter('medical_issue', value)
                                        }
                                        placeholder='Medical Issue'
                                    />
                                </div>
                            </div>
                        )}
                    <input
                        type='date'
                        name='start_date'
                        className='h-[36px] rounded border border-[#E2E8F0] px-2 text-sm'
                        value={dateRange.start_date}
                        onChange={(event) =>
                            handleDateRange('start_date', event.target.value)
                        }
                    />
                    <span className='text-sm text-gray-500'>to</span>
                    <input
                        type='date'
                        name='end_date'
                        className='h-[36px] rounded border border-[#E2E8F0] px-2 text-sm'
                        value={dateRange.end_date}
                        onChange={(event) =>
                            handleDateRange('end_date', event.target.value)
                        }
                    />
                    <button
                        type='button'
                        onClick={() => refreshCampaignApplications()}
                        className={`text-gray-600 hover:bg-gray-50 inline-flex shrink-0 items-center justify-center rounded p-2 transition-colors duration-300 ${
                            isFetching ? 'cursor-wait opacity-60' : ''
                        }`}
                        title='Refresh data'
                        disabled={isFetching}
                        aria-busy={isFetching}
                    >
                        <FaSync
                            className={`text-base ${
                                isFetching ? 'animate-spin' : ''
                            }`}
                        />
                    </button>
                    {hasActiveFilters && (
                        <button
                            type='button'
                            onClick={handleClearFilters}
                            className='inline-flex shrink-0 items-center gap-1.5 rounded border border-[#E2E8F0] px-3 py-1.5 text-xs text-gray-500 transition-colors hover:border-danger hover:text-danger'
                            title='Clear all filters'
                        >
                            <FaTimes className='text-[10px]' />
                            Clear Filters
                        </button>
                    )}
                </div>

                <div
                    className='flex items-center justify-between border-t border-stroke px-8 py-1'
                    style={{ fontSize: '12px' }}
                >
                    <p>
                        Showing {Math.min(pageSize, (applicationResults?.count || 0) - (page - 1) * pageSize)} of {applicationResults?.count || 0} applications
                    </p>
                </div>

                <div className='overflow-x-auto'>
                    <table className='datatable-table datatable-one w-full table-auto border-collapse break-words px-4 md:px-8 2xl:table-fixed'>
                        <thead className='border-separate px-4'>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr
                                    className='border-t border-stroke bg-whiten'
                                    style={{ fontSize: '14px' }}
                                    key={headerGroup.id}
                                >
                                    {headerGroup.headers.map((header) => {
                                        const useChevron =
                                            header.column.id === sortBy;
                                        let chevron = null;
                                        if (useChevron) {
                                            if (sort === Sort.ASC) {
                                                chevron = (
                                                    <svg
                                                        xmlns='http://www.w3.org/2000/svg'
                                                        viewBox='0 0 20 20'
                                                        fill='currentColor'
                                                        className='h-5 w-5'
                                                    >
                                                        <path
                                                            fillRule='evenodd'
                                                            d='M9.47 6.47a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 1 1-1.06 1.06L10 8.06l-3.72 3.72a.75.75 0 0 1-1.06-1.06l4.25-4.25Z'
                                                            clipRule='evenodd'
                                                        />
                                                    </svg>
                                                );
                                            } else if (sort === Sort.DESC) {
                                                chevron = (
                                                    <svg
                                                        xmlns='http://www.w3.org/2000/svg'
                                                        viewBox='0 0 20 20'
                                                        fill='currentColor'
                                                        className='h-5 w-5'
                                                    >
                                                        <path
                                                            fillRule='evenodd'
                                                            d='M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z'
                                                            clipRule='evenodd'
                                                        />
                                                    </svg>
                                                );
                                            }
                                        }

                                        return (
                                            <th
                                                style={{
                                                    paddingTop: '16px',
                                                    paddingBottom: '16px',
                                                    width: header.column
                                                        .columnDef.size
                                                        ? `${header.column.columnDef.size}px`
                                                        : 'auto',
                                                    minWidth: header.column
                                                        .columnDef.minSize
                                                        ? `${header.column.columnDef.minSize}px`
                                                        : 'auto',
                                                    maxWidth: header.column
                                                        .columnDef.maxSize
                                                        ? `${header.column.columnDef.maxSize}px`
                                                        : 'auto',
                                                    ...header.column.columnDef
                                                        .meta?.style,
                                                }}
                                                key={header.id}
                                            >
                                                <div className='flex items-center'>
                                                    <span
                                                        onClick={() =>
                                                            handleSort(
                                                                header.column.id
                                                            )
                                                        }
                                                        className='cursor-pointer'
                                                    >
                                                        {header.isPlaceholder
                                                            ? null
                                                            : flexRender(
                                                                  header.column
                                                                      .columnDef
                                                                      .header,
                                                                  header.getContext()
                                                              )}
                                                    </span>
                                                    <div className='ml-2 inline-flex flex-col space-y-[2px]'>
                                                        <span className='inline-block'>
                                                            {chevron}
                                                        </span>
                                                    </div>
                                                </div>
                                            </th>
                                        );
                                    })}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {isLoading
                                ? Array.from({ length: 10 }).map((_, index) => (
                                      <tr key={index}>
                                          {columns.map((_column, colIndex) => (
                                              <td
                                                  key={colIndex}
                                                  className='border-b border-stroke px-4 py-2 dark:border-strokedark'
                                              >
                                                  <div className='max-w-sm animate-pulse'>
                                                      <div className='h-2 max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                                                  </div>
                                              </td>
                                          ))}
                                      </tr>
                                  ))
                                : table.getRowModel().rows.map((row) => (
                                      <tr
                                          className='border-t border-stroke'
                                          key={row.id}
                                      >
                                          {row.getVisibleCells().map((cell) => (
                                              <td
                                                  style={{
                                                      paddingTop: '16px',
                                                      paddingBottom: '16px',
                                                      width: cell.column
                                                          .columnDef.size
                                                          ? `${cell.column.columnDef.size}px`
                                                          : 'auto',
                                                      minWidth: cell.column
                                                          .columnDef.minSize
                                                          ? `${cell.column.columnDef.minSize}px`
                                                          : 'auto',
                                                      maxWidth: cell.column
                                                          .columnDef.maxSize
                                                          ? `${cell.column.columnDef.maxSize}px`
                                                          : 'auto',
                                                      ...cell.column.columnDef
                                                          .meta?.style,
                                                  }}
                                                  key={cell.id}
                                              >
                                                  {flexRender(
                                                      cell.column.columnDef
                                                          .cell,
                                                      cell.getContext()
                                                  )}
                                              </td>
                                          ))}
                                      </tr>
                                  ))}
                        </tbody>
                    </table>
                </div>

                <Pagination page={page} setPage={setPage} pages={pages} totalCount={applicationResults?.count} />
            </section>
        </>
    );
};

export default CampaignApplications;
